import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import { IStringLocalizer } from '../../../../utilities/IStringLocalizer';

const ANSWERED_COUNT_PATTERN = /^(\d+)\s+of\s+(\d+)\s+answered$/i;
const COUNT_PATTERN = /^(\d+)\s+of\s+(\d+)$/i;
const DOCUMENT_COUNT_PATTERN = /^document\s+(\d+)\s+of\s+(\d+)$/i;

const formatStringFallback = (formatString: string, ...args: any[]): string => (
    formatString.replace(/\{(\d+)\}/g, (_match, indexText) => {
        const index = Number.parseInt(indexText, 10);
        const value = args[index];
        return value === null || value === undefined ? '' : String(value);
    })
);

export const localizeFormatString = (
    localizer: IStringLocalizer | undefined,
    formatString: string,
    ...args: any[]
): string => (
    localizer?.localizeStringFormat(formatString, ...args)
    || formatStringFallback(formatString, ...args)
);

export const localizeAiAgentState = (
    agentState: string | null | undefined,
    localize: (key: string) => string,
    localizeFormat: (formatString: string, ...args: any[]) => string
): string => {
    const trimmedAgentState = agentState?.trim() ?? '';

    if (trimmedAgentState.length === 0) {
        return '';
    }

    const answeredMatch = trimmedAgentState.match(ANSWERED_COUNT_PATTERN);
    if (answeredMatch) {
        return localizeFormat("{0} of {1} answered", answeredMatch[1], answeredMatch[2]);
    }

    const countMatch = trimmedAgentState.match(COUNT_PATTERN);
    if (countMatch) {
        return localizeFormat("{0} of {1}", countMatch[1], countMatch[2]);
    }

    const documentCountMatch = trimmedAgentState.match(DOCUMENT_COUNT_PATTERN);
    if (documentCountMatch) {
        return localizeFormat("Document {0} of {1}", documentCountMatch[1], documentCountMatch[2]);
    }

    return localize(trimmedAgentState);
};

/**
 * Custom hook for localizing strings with a clean API
 * 
 * @param localizer - Optional IStringLocalizer override
 * @returns Memoized localize function using the override or the app-wide AppServices localizer
 */
export const useLocalizer = (localizer?: IStringLocalizer) => {
    const appLocalizer = useSelector((state: ApplicationState) => state.appServices);
    const resolvedLocalizer = localizer ?? appLocalizer ?? undefined;
    const localize = useCallback(
        (key: string): string => {
            return resolvedLocalizer?.localizeString(key) || key;
        },
        [resolvedLocalizer]
    );

    return localize;
};

export const useLocalizerFormat = (localizer?: IStringLocalizer) => {
    const appLocalizer = useSelector((state: ApplicationState) => state.appServices);
    const resolvedLocalizer = localizer ?? appLocalizer ?? undefined;
    const localizeFormat = useCallback(
        (formatString: string, ...args: any[]): string => (
            localizeFormatString(resolvedLocalizer, formatString, ...args)
        ),
        [resolvedLocalizer]
    );

    return localizeFormat;
};
